import type { JSX, ReactNode } from 'react';
import { CheckCircle2, Eye, Package, Play, Sparkles } from 'lucide-react';

interface PreviewExperiencePanelProps {
  testId: string;
  title: string;
  subtitle: string;
  accent?: string;
  creates: string[];
  receives: string[];
  example: ReactNode;
  benefit: string;
  interactiveLabel?: string;
  onInteractive?: () => void;
}

export function PreviewExperiencePanel({
  testId,
  title,
  subtitle,
  accent = '#35d7f0',
  creates,
  receives,
  example,
  benefit,
  interactiveLabel,
  onInteractive,
}: PreviewExperiencePanelProps): JSX.Element {
  const panel = 'rgba(6,15,35,0.82)';
  const border = `${accent}38`;
  const muted = 'rgba(238,247,255,0.62)';
  const text = '#eef7ff';

  return (
    <section
      data-testid={testId}
      style={{
        background: `linear-gradient(135deg, ${accent}10, rgba(7,16,34,0.82) 58%, rgba(0,0,0,0.22))`,
        border: `1px solid ${border}`,
        borderRadius: 14,
        padding: 16,
        marginBottom: 18,
        boxShadow: `0 18px 54px ${accent}0d`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ minWidth: 260, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent, fontFamily: "'JetBrains Mono','Space Mono',monospace", fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6 }}>
            <Eye size={13}/> Preview First
          </div>
          <h2 style={{ margin: 0, color: text, fontSize: 22, lineHeight: 1.12, fontWeight: 950 }}>{title}</h2>
          <p style={{ margin: '7px 0 0', color: muted, fontSize: 12.5, lineHeight: 1.55, maxWidth: 760 }}>{subtitle}</p>
        </div>
        {interactiveLabel && onInteractive && (
          <button
            data-testid={`${testId}-interactive-button`}
            type="button"
            onClick={onInteractive}
            style={{ border: 'none', borderRadius: 10, background: accent, color: '#06111f', padding: '10px 14px', fontWeight: 950, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}
          >
            <Play size={14}/> {interactiveLabel}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 0.9fr) minmax(260px, 1.2fr) minmax(240px, 1fr)', gap: 12 }} className="r2-preview-experience-grid">
        <div data-testid={`${testId}-creates`} style={{ background: panel, border: `1px solid ${border}`, borderRadius: 10, padding: 13 }}>
          <div style={{ color: accent, fontFamily: "'JetBrains Mono','Space Mono',monospace", fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 9 }}>What it creates</div>
          {creates.map((item) => <PreviewRow key={item} item={item} accent={accent}/>) }
        </div>

        <div data-testid={`${testId}-example-output`} style={{ background: 'rgba(0,0,0,0.22)', border: `1px solid ${border}`, borderRadius: 10, padding: 13, minHeight: 148 }}>
          <div style={{ color: accent, fontFamily: "'JetBrains Mono','Space Mono',monospace", fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 9 }}>Example output</div>
          {example}
        </div>

        <div data-testid={`${testId}-receives`} style={{ background: panel, border: `1px solid ${border}`, borderRadius: 10, padding: 13 }}>
          <div style={{ color: accent, fontFamily: "'JetBrains Mono','Space Mono',monospace", fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 9 }}>Customer receives</div>
          {receives.map((item) => <PreviewRow key={item} item={item} accent={accent}/>) }
          <div data-testid={`${testId}-benefit`} style={{ marginTop: 10, padding: 10, borderRadius: 8, background: `${accent}0d`, border: `1px solid ${border}`, color: muted, fontSize: 11.5, lineHeight: 1.5 }}>
            <Sparkles size={12} style={{ color: accent, marginRight: 6, display: 'inline' }}/>{benefit}
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 980px){.r2-preview-experience-grid{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}

function PreviewRow({ item, accent }: { item: string; accent: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: 'rgba(238,247,255,0.72)', fontSize: 11.5, lineHeight: 1.45, marginBottom: 7 }}>
      <CheckCircle2 size={12} style={{ color: accent, flexShrink: 0, marginTop: 2 }}/>
      <span>{item}</span>
    </div>
  );
}

export function MiniSheetPreview({ accent = '#35d7f0', rows = 5 }: { accent?: string; rows?: number }): JSX.Element {
  return (
    <div style={{ border: `1px solid ${accent}55`, background: '#07101f', borderRadius: 8, overflow: 'hidden', minHeight: 132 }}>
      <div style={{ height: 28, borderBottom: `1px solid ${accent}35`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', color: accent, fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em' }}>
        <span>R2 OUTPUT PREVIEW</span><Package size={12}/>
      </div>
      <div style={{ padding: 10, display: 'grid', gap: 7 }}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 72px', gap: 8, alignItems: 'center' }}>
            <div style={{ height: 8, borderRadius: 2, background: `${accent}${i === 0 ? 'cc' : '55'}` }}/>
            <div style={{ height: 8, borderRadius: 2, background: 'rgba(238,247,255,0.14)' }}/>
            <div style={{ height: 8, borderRadius: 2, background: 'rgba(244,201,93,0.25)' }}/>
          </div>
        ))}
      </div>
    </div>
  );
}